import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, CalendarDays, ArrowLeft, Bookmark, Flag, Mail, Phone, ImageOff,
  BadgeCheck, Gift, ShieldAlert, Loader2, Eye, MessageSquare,
} from "lucide-react";
import AnnonceCard from "@/components/annonces/AnnonceCard";
import FbGroupShare from "@/components/FbGroupShare";
import ShareButton from "@/components/ShareButton";
import LikeButton from "@/components/LikeButton";
import DemoBanner from "@/components/DemoBanner";
import FeaturedPartners from "@/components/FeaturedPartners";
import PressOffers from "@/components/PressOffers";
import EventBrandMentions from "@/components/EventBrandMentions";

const API = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { v: "forbidden", l: "Contenu interdit / illégal" },
  { v: "scam", l: "Soupçon d'arnaque" },
  { v: "bad-photo", l: "Photo inappropriée" },
  { v: "wrong-category", l: "Catégorie incorrecte" },
  { v: "duplicate", l: "Annonce en double" },
  { v: "personal-info", l: "Coordonnées personnelles exposées" },
  { v: "pro-misidentified", l: "Annonce pro mal identifiée" },
  { v: "other", l: "Autre motif" },
];

export default function AnnonceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [fav, setFav] = useState(false);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactMsg, setContactMsg] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [sending, setSending] = useState(false);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("forbidden");
  const [reportMsg, setReportMsg] = useState("");

  const [anonName, setAnonName] = useState("");
  const [anonEmail, setAnonEmail] = useState("");
  const isGuest = !(user && user !== false);
  const anonValid = () => {
    if (!isGuest) return true;
    if (!anonEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(anonEmail.trim())) {
      toast.error("Ajoutez votre e-mail pour continuer."); return false;
    }
    return true;
  };

  useEffect(() => {
    setA(null); setNotFound(false); setActiveImg(0);
    api.get(`/annonces/${slug}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!a || !user || user === false) return;
    api.get("/me/annonces-favoris").then((r) => setFav((r.data?.ids || []).includes(a.id))).catch(() => {});
  }, [a, user]);

  useSeo({
    title: a ? `${a.title} — ${a.price_label} à ${a.city} | PROXIreduc-28` : "Annonce | PROXIreduc-28",
    description: a ? (a.description || "").slice(0, 160) : "",
    canonicalPath: `/petites-annonces/annonce/${slug}`,
    image: a?.main_image || "/og-bons-plans.jpg",
    type: "article",
  });

  const requireAuth = () => {
    if (!user || user === false) { toast.message("Connectez-vous pour continuer."); navigate("/connexion"); return false; }
    return true;
  };

  const toggleFav = async () => {
    if (!requireAuth()) return;
    try {
      if (fav) { await api.delete(`/me/annonces-favoris/${a.id}`); setFav(false); }
      else { await api.post(`/me/annonces-favoris/${a.id}`); setFav(true); toast.success("Enregistré dans vos favoris"); }
    } catch { toast.error("Action impossible"); }
  };

  const sendContact = async () => {
    if (contactMsg.trim().length < 5) { toast.error("Message trop court."); return; }
    if (!anonValid()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/annonces/${a.id}/contact`, { message: contactMsg, sender_phone: contactPhone, name: anonName, email: anonEmail });
      toast.success(data.account_created
        ? "Message envoyé ✅ Un espace membre gratuit a été créé — vos identifiants vous ont été envoyés par e-mail."
        : (data.delivered ? "Message envoyé au vendeur ✅" : "Message enregistré"));
      setContactOpen(false); setContactMsg(""); setContactPhone("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };

  const sendMessage = async () => {
    if (msgText.trim().length < 1) { toast.error("Message vide."); return; }
    if (!anonValid()) return;
    setSendingMsg(true);
    try {
      const { data } = await api.post(`/annonces/${a.id}/message`, { message: msgText, name: anonName, email: anonEmail });
      toast.success(data.account_created
        ? "Message envoyé ✅ Un espace membre gratuit a été créé — retrouvez la conversation avec les identifiants reçus par e-mail."
        : "Message envoyé ! Suivez la conversation dans « Mes messages ».");
      setMsgOpen(false); setMsgText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setSendingMsg(false); }
  };

  const sendReport = async () => {
    if (!anonValid()) return;
    try {
      await api.post(`/annonces/${a.id}/report`, { reason: reportReason, message: reportMsg, name: anonName, email: anonEmail });
      toast.success("Signalement transmis à la modération. Merci.");
      setReportOpen(false); setReportMsg("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="annonce-404">
      <h1 className="font-heading text-2xl font-bold">Annonce introuvable ou retirée</h1>
      <Link to="/petites-annonces" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold"><ArrowLeft size={16} /> Retour aux annonces</Link>
    </div>
  );
  if (!a) return <div className="max-w-2xl mx-auto px-4 py-20 text-slate-500">Chargement…</div>;

  const isOwner = user && user !== false && user.id === a.owner_id;
  const images = a.images && a.images.length ? a.images : [];
  const sold = a.status === "sold" || a.status === "given";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-testid="annonce-detail">
      <Link to="/petites-annonces" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638]"><ArrowLeft size={14} /> Toutes les annonces</Link>
      {a.demo && <DemoBanner className="mt-4" />}

      <div className="mt-4 grid lg:grid-cols-5 gap-6">
        {/* Galerie */}
        <div className="lg:col-span-3">
          <div className="aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden relative" data-testid="annonce-gallery">
            {images.length ? (
              <img src={images[activeImg]} alt={a.title} className="w-full h-full object-contain bg-slate-50" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-300"><ImageOff size={48} /></div>
            )}
            {sold && (
              <div className="absolute inset-0 bg-black/50 grid place-items-center">
                <span className="bg-white text-slate-900 font-extrabold text-xl px-6 py-2 rounded-full rotate-[-8deg]">
                  {a.status === "given" ? "DONNÉ" : "VENDU"}
                </span>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto" data-testid="annonce-thumbs">
              {images.map((u, i) => (
                <button key={u} onClick={() => setActiveImg(i)}
                        className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === activeImg ? "border-[#E05638]" : "border-transparent"}`}
                        data-testid={`annonce-thumb-${i}`}>
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Infos */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Link to={`/petites-annonces/categorie/${a.category_slug}`} className="px-2 py-0.5 rounded-full bg-orange-50 text-[#E05638] font-semibold">{a.category_name}</Link>
            {a.is_pro && <span className="inline-flex items-center gap-1 bg-slate-900 text-white px-2 py-0.5 rounded-full"><BadgeCheck size={11} /> Vendeur pro</span>}
            {a.price_type === "free" && <span className="inline-flex items-center gap-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full"><Gift size={11} /> Don</span>}
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="annonce-title">{a.title}</h1>
          <div className="font-heading text-3xl font-extrabold text-[#E05638]" data-testid="annonce-price">{a.price_label}</div>

          <div className="text-sm text-slate-600 space-y-1.5">
            <div className="flex items-center gap-2"><MapPin size={15} className="text-slate-400" /> {a.city}</div>
            {a.published_at && <div className="flex items-center gap-2"><CalendarDays size={15} className="text-slate-400" /> {new Date(a.published_at).toLocaleDateString("fr-FR")}</div>}
            {a.condition_label && <div className="flex items-center gap-2"><ShieldAlert size={15} className="text-slate-400" /> État : <strong>{a.condition_label}</strong></div>}
            <div className="flex items-center gap-2"><Eye size={15} className="text-slate-400" /> {a.views} vue{a.views > 1 ? "s" : ""}</div>
            {a.hand_delivery && <div className="text-emerald-700">✓ Remise en main propre</div>}
          </div>

          {!isOwner && !sold && (
            <div className="space-y-2">
              <Button onClick={() => setMsgOpen(true)} className="w-full btn-shine text-white h-11" data-testid="annonce-message-btn">
                <MessageSquare size={16} className="mr-2" /> Envoyer un message
              </Button>
              <button onClick={() => setContactOpen(true)} className="w-full text-xs text-slate-500 hover:text-[#E05638] inline-flex items-center justify-center gap-1" data-testid="annonce-contact-btn">
                <Mail size={13} /> ou contacter par e-mail
              </button>
            </div>
          )}
          {a.show_phone && a.contact_phone && (
            <a href={`tel:${a.contact_phone}`} className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-300 font-semibold text-slate-800 hover:border-[#E05638]" data-testid="annonce-phone-btn">
              <Phone size={16} /> {a.contact_phone}
            </a>
          )}
          {isOwner && (
            <Link to={`/petites-annonces/modifier/${a.id}`} className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-300 font-semibold text-slate-800 hover:border-[#E05638]" data-testid="annonce-edit-btn">
              Modifier mon annonce
            </Link>
          )}

          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <LikeButton type="annonce" id={a.id} liked={a.liked} count={a.likes} testId="annonce-like-btn" />
            <Button variant="outline" size="sm" onClick={toggleFav} data-testid="annonce-fav-btn" className={fav ? "text-[#E05638] border-orange-300" : ""}>
              <Bookmark size={15} className={`mr-1.5 ${fav ? "fill-[#E05638] text-[#E05638]" : ""}`} /> {fav ? "Enregistré" : "Enregistrer"}
            </Button>
            <ShareButton url={`${API}/api/annonce-vitrine/${a.slug}`} title={a.title} text={`${a.price_label} · ${a.title} — à ${a.city}`} testId="annonce-share-btn" className="h-9" />
            <Button variant="outline" size="sm" onClick={() => setReportOpen(true)} data-testid="annonce-report-btn" aria-label="Signaler cette annonce" title="Signaler cette annonce" className="text-slate-500"><Flag size={15} /></Button>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Conseil sécurité : privilégiez la remise en main propre et ne versez jamais d'acompte avant d'avoir vu l'objet.
          </p>
        </div>
      </div>

      {/* Description */}
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid="annonce-description">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">Description</h2>
        <p className="text-slate-700 whitespace-pre-line leading-relaxed text-sm">{a.description}</p>
        {(a.brand || a.model) && (
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600 border-t border-slate-100 pt-3">
            {a.brand && <div><span className="text-slate-400">Marque :</span> <strong>{a.brand}</strong></div>}
            {a.model && <div><span className="text-slate-400">Modèle :</span> <strong>{a.model}</strong></div>}
          </div>
        )}
        {a.extra_info && <p className="mt-3 text-sm text-slate-500 italic">{a.extra_info}</p>}
        <div className="mt-4 text-xs text-slate-500">Proposé par <strong>{a.owner_name}</strong></div>
      </div>

      {!sold && (
        <FbGroupShare
          text={`${a.price_label} · ${a.title} — à ${a.city}`}
          shareUrl={`${API}/api/annonce-vitrine/${a.slug}`}
          cityHint={a.city}
        />
      )}

      {/* Similaires */}
      {a.similar?.length > 0 && (
        <div className="mt-10" data-testid="annonce-similar">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Annonces similaires</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {a.similar.map((s) => <AnnonceCard key={s.id} a={s} testIdPrefix="similar" />)}
          </div>
        </div>
      )}

      {/* Blocs partenaires / offres / marque — tout en bas de la page */}
      <FeaturedPartners limit={5} testId="annonce-featured-partners" city={a.city || ""} />
      <PressOffers limit={4} testId="annonce-press-offers" city={a.city || ""} />
      <div className="mt-12 pt-8 border-t border-slate-100">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Partenaires</div>
        <EventBrandMentions testId="annonce-brand-mentions" />
      </div>

      {/* Dialog contact */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-testid="annonce-contact-dialog">
          <DialogHeader><DialogTitle>Contacter le vendeur</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Votre message sera transmis par e-mail. Votre adresse e-mail servira de réponse ; l'e-mail du vendeur reste masqué.</p>
          <Textarea rows={5} value={contactMsg} onChange={(e) => setContactMsg(e.target.value)}
                    placeholder="Bonjour, votre annonce est-elle toujours disponible ?…" data-testid="annonce-contact-message" />
          <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Votre téléphone (facultatif)" data-testid="annonce-contact-phone" />
          {isGuest && (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="annonce-contact-anon">
              <Input value={anonName} onChange={(e) => setAnonName(e.target.value)} placeholder="Votre prénom (facultatif)" data-testid="annonce-contact-anon-name" />
              <Input type="email" value={anonEmail} onChange={(e) => setAnonEmail(e.target.value)} placeholder="Votre e-mail" data-testid="annonce-contact-anon-email" />
              <p className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">Votre e-mail sert à recevoir la réponse — un espace membre gratuit est créé, accès envoyé par e-mail.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)}>Annuler</Button>
            <Button onClick={sendContact} disabled={sending} className="btn-shine text-white" data-testid="annonce-contact-send">
              {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Mail size={15} className="mr-1.5" />} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog message interne */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent data-testid="annonce-message-dialog">
          <DialogHeader><DialogTitle>Envoyer un message au vendeur</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Échangez en toute sécurité via la messagerie interne. Retrouvez la conversation dans « Mes messages ».</p>
          <Textarea rows={5} value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Bonjour, votre annonce est-elle toujours disponible ?…" data-testid="annonce-message-text" />
          {isGuest && (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="annonce-message-anon">
              <Input value={anonName} onChange={(e) => setAnonName(e.target.value)} placeholder="Votre prénom (facultatif)" data-testid="annonce-message-anon-name" />
              <Input type="email" value={anonEmail} onChange={(e) => setAnonEmail(e.target.value)} placeholder="Votre e-mail" data-testid="annonce-message-anon-email" />
              <p className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">Un espace membre gratuit est créé pour suivre la conversation — accès envoyé par e-mail.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)}>Annuler</Button>
            <Button onClick={sendMessage} disabled={sendingMsg} className="btn-shine text-white" data-testid="annonce-message-send">
              {sendingMsg ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <MessageSquare size={15} className="mr-1.5" />} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog signalement */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="annonce-report-dialog">
          <DialogHeader><DialogTitle>Signaler cette annonce</DialogTitle></DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="annonce-report-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{REPORT_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={3} value={reportMsg} onChange={(e) => setReportMsg(e.target.value)} placeholder="Précisions (facultatif)…" data-testid="annonce-report-message" />
          {isGuest && (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="annonce-report-anon">
              <Input value={anonName} onChange={(e) => setAnonName(e.target.value)} placeholder="Votre prénom (facultatif)" data-testid="annonce-report-anon-name" />
              <Input type="email" value={anonEmail} onChange={(e) => setAnonEmail(e.target.value)} placeholder="Votre e-mail" data-testid="annonce-report-anon-email" />
              <p className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">Votre e-mail permet de suivre le signalement — un espace membre gratuit est créé, accès envoyé par e-mail.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button onClick={sendReport} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="annonce-report-send"><Flag size={15} className="mr-1.5" /> Signaler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
